import addFormats from 'ajv-formats';
import Ajv2019, { ErrorObject } from 'ajv/dist/2019';
import draft7MetaSchema from 'ajv/dist/refs/json-schema-draft-07.json';
import { parse } from 'lossless-json';

import { DataType } from '../components';
import { VariableInfo } from '@/lib/PlannerDTOs';
import { GenericValue } from '@/lib/PlannerMetadata';

// Create AJV instance with support for both draft-07 and draft-2019-09
// See: https://ajv.js.org/guide/schema-language.html#draft-07-and-draft-06
const ajv = new Ajv2019();
addFormats(ajv);
ajv.addMetaSchema(draft7MetaSchema); // Support draft-07 schemas in addition to draft-2019-09

export const validateValue = (
  variable: VariableInfo,
  val: GenericValue,
): string => {
  const jsonSchema = variable?.metadata?.jsonSchema;

  switch (variable.type) {
    case DataType.JSON:
    case DataType.MAP:
      {
        if (val.string) {
          try {
            parse(val.string);
          } catch (e) {
            return e.message;
          }
        } else {
          return '';
        }
      }
      break;
    case DataType.FLOAT64:
    case DataType.INT64:
      if (val.value == null) return '';
      break;
    default:
      break;
  }

  if (!jsonSchema) return '';

  const schema = JSON.parse(jsonSchema);
  const { value, string } = val;

  switch (variable.type) {
    case DataType.STRING: {
      const minLength = schema?.minLength;
      const maxLength = schema?.maxLength;
      const values = schema?.enum;

      if (
        (minLength != null && minLength > string?.length) ||
        (maxLength != null && maxLength < string?.length)
      ) {
        if (minLength != null && maxLength != null)
          return `Length of string must be between ${minLength} and ${maxLength} characters`;

        if (minLength != null)
          return `Length of string must be no less than ${minLength} characters`;

        return `Length of string must be no longer than ${maxLength} characters`;
      }

      if (values && !values.includes(string)) return `Value is invalid.`;

      return '';
    }
    case DataType.FLOAT64:
    case DataType.INT64: {
      const min = schema?.minimum;
      const max = schema?.maximum;
      const values = schema?.enum;

      if ((min != null && min > value) || (max != null && max < value)) {
        if (min != null && max != null)
          return `Value must be between ${min} and ${max}`;

        if (min != null) return `Value cannot be lesser than ${min}`;

        return `Value cannot be greater than ${max}`;
      }

      if (values && !values.includes(value)) return `Value is invalid.`;

      return '';
    }

    case DataType.JSON:
    case DataType.MAP: {
      if (!string) return 'Invalid JSON object';

      try {
        const isValid = ajv.validate(schema, JSON.parse(string));

        if (!isValid) {
          return formatErrorMessage(ajv.errors);
        }
      } catch (e) {
        console.error(e);
      }

      return '';
    }
  }
};

function formatErrorMessage(errors: ErrorObject[]): string {
  for (const err of errors as ErrorObject[]) {
    switch (err.keyword) {
      case 'enum':
        return `${err.instancePath} ${err.message} [${err.params.allowedValues.map((value) => `'${value}'`).join(',')}]`;
      default:
        return `${err.instancePath} ${err.message}`;
    }
  }
}
